import type { EditorState } from '../state/types';
import { removeSilenceSelection, silenceRemovalRanges, silenceTrackScope, type SilenceRemoval } from '../state/actions/remove-silence';
import { findAllLinkedItems } from '../utils/find-linked-items';
import type { SilenceDetectionParams, SilenceSegment } from '../utils/silence-detection';

export interface SilenceReviewTarget {
  itemId: string;
  label: string;
  audioUrl: string;
  sourceStart: number;
  sourceDuration: number;
  playbackRate: number;
  from: number;
  durationInFrames: number;
}
export interface SilenceCandidate {
  id: string;
  target: SilenceReviewTarget;
  segment: SilenceSegment;
}
export interface SilenceReviewSession {
  before: EditorState['undoableState'];
  linking: boolean;
  selection: readonly string[];
  candidates: SilenceCandidate[];
}
export const isSilenceReviewCurrent = (state: EditorState, session: Pick<SilenceReviewSession, 'before' | 'linking' | 'selection'>) =>
  state.undoableState === session.before && state.isLinkingEnabled === session.linking &&
  state.selectedItems.length === session.selection.length && state.selectedItems.every((id, index) => id === session.selection[index]) &&
  !state.timelineMoveActive && !state.timelineInputOwner && !state.itemsBeingTrimmed.length;

export function silenceReviewTargets(state: EditorState, ids: readonly string[], resolveUrl?: (assetId: string) => string | null): SilenceReviewTarget[] {
  const eligibleIds = ids.filter((itemId) => silenceTrackScope(state, itemId).length > 0);
  const targetIds = state.isLinkingEnabled
    ? findAllLinkedItems({ targetItemIds: eligibleIds, tracks: state.undoableState.tracks, items: state.undoableState.items })
    : eligibleIds;
  return [...new Set(targetIds)].flatMap((itemId) => {
    const item = state.undoableState.items[itemId];
    if (!item || (item.type !== 'audio' && item.type !== 'video') || !silenceTrackScope(state, itemId).length) return [];
    const asset = state.undoableState.assets[item.assetId];
    if (!asset || (asset.type !== 'audio' && asset.type !== 'video') || (asset.type === 'video' && asset.hasAudioTrack === false)) return [];
    const audioUrl = resolveUrl ? resolveUrl(asset.id) : (asset.remoteUrl ?? asset.staticPath);
    const playbackRate = item.playbackRate;
    if (!audioUrl || !Number.isFinite(playbackRate) || playbackRate <= 0 || state.undoableState.fps <= 0) return [];
    const sourceStart = item.type === 'video' ? item.videoStartFromInSeconds : item.audioStartFromInSeconds;
    if (!Number.isFinite(sourceStart) || sourceStart < 0 || item.durationInFrames <= 0) return [];
    return [{ itemId, label: asset.filename, audioUrl, playbackRate, from: item.from,
      durationInFrames: item.durationInFrames,
      sourceStart,
      sourceDuration: item.durationInFrames / state.undoableState.fps * playbackRate }];
  });
}

export async function detectSilenceReview(state: EditorState, targets: SilenceReviewTarget[], params: Pick<SilenceDetectionParams, 'minSilenceDurationMs' | 'silenceThresholdDb'>, detect: (params: SilenceDetectionParams) => Promise<SilenceSegment[]>): Promise<SilenceReviewSession> {
  const fps = state.undoableState.fps;
  const candidates: SilenceCandidate[] = [];
  const seen = new Set<string>();
  for (const target of targets) {
    const segments = await detect({ audioUrl: target.audioUrl, startInSeconds: target.sourceStart,
      durationInSeconds: target.sourceDuration,
      minSilenceDurationMs: params.minSilenceDurationMs * target.playbackRate,
      silenceThresholdDb: params.silenceThresholdDb });
    for (const source of segments) {
      const start = Math.max(0, Math.ceil(source.startInSeconds / target.playbackRate * fps - 1e-7));
      const end = Math.min(target.durationInFrames, Math.floor(source.endInSeconds / target.playbackRate * fps + 1e-7));
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
      const scope = silenceTrackScope(state, target.itemId).sort().join(',');
      const key = `${scope}:${target.from + start}:${target.from + end}`;
      if (seen.has(key)) continue;
      seen.add(key);
      candidates.push({ id: key, target, segment: { startInSeconds: start / fps, endInSeconds: end / fps } });
    }
  }
  return { before: state.undoableState, linking: state.isLinkingEnabled, selection: [...state.selectedItems], candidates };
}

export function selectedSilenceRemovals(session: SilenceReviewSession, selected: ReadonlySet<string>): SilenceRemoval[] {
  return session.candidates.filter((candidate) => selected.has(candidate.id))
    .map((candidate) => ({ itemId: candidate.target.itemId, silenceSegments: [candidate.segment] }));
}
export function estimatedSilenceSeconds(session: SilenceReviewSession, selected: ReadonlySet<string>): number {
  const state = { undoableState: session.before, isLinkingEnabled: session.linking } as EditorState;
  const ranges = [...silenceRemovalRanges(state, selectedSilenceRemovals(session, selected)).values()];
  // Parallel tracks shorten together, rather than adding video and audio durations.
  return Math.max(0, ...ranges.map((track) => track.reduce((sum, range) => sum + range.end - range.start, 0))) / session.before.fps;
}
export function applySilenceReview(state: EditorState, session: SilenceReviewSession, selected: ReadonlySet<string>): EditorState {
  return isSilenceReviewCurrent(state, session)
    ? removeSilenceSelection(state, selectedSilenceRemovals(session, selected)) : state;
}
export function silencePreviewWindow(candidate: SilenceCandidate, contextSeconds = 0.4) {
  const { target, segment } = candidate;
  return {
    start: target.sourceStart + Math.max(0, segment.startInSeconds - contextSeconds) * target.playbackRate,
    end: target.sourceStart + Math.min(target.sourceDuration, (segment.endInSeconds + contextSeconds) * target.playbackRate),
  };
}
